import { Checkbox } from '@carbon/react';
import { useAtom } from 'jotai';
import { startCase } from 'lodash-es';
import { MapUserConfig, MapUserConfigAtom } from './types';
import classNames from 'classnames';
import { WELL_STATUS_COLOR_MAP } from 'utils/well-status';

import style from './map.module.scss';
import { CollapsibleSection } from './collapsible-section';
import { StructureGridSelector } from '../structure-grids/structure-grid-selector';
import { FocusAreaSelector } from './focus-areas/focus-area-selector';
import { PipelineSelector } from './pipelines/pipeline-selector';
import { DrillingRigSelector } from './drilling-rigs/drilling-rig-selector';

const LayerOption = ({
  layerKey,
  mapConfigAtom,
}: {
  layerKey:
    | 'wells'
    | 'developmentAreas'
    | 'basinTier'
    | 'wellsV2'
    | 'titleEntityAssets';
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);

  return (
    <>
      {Object.keys(mapConfig[layerKey].options).map((layer) => {
        const checkboxClassName = classNames({
          [style.surfaceHole]: layer === 'surfaceHole',
          [style.bottomHole]: layer === 'bottomHole',
          [style.stick]: layer === 'stick',
          [style.survey]: layer === 'survey',
          [style.vertical]: layer === 'vertical',
          [style.permit]: layer === 'permit',
          [style.duc]: layer === 'duc',
          [style.producing]: layer === 'producing',
          [style.shutIn]: layer === 'shutIn',
          [style.expiredPermit]: layer === 'expiredPermit',
          [style.inactive]: layer === 'inactive',
          [style.pricing]: layer === 'pricing',
          [style.units]: layer === 'units',
          [style.basin]: layer === 'basin',
          [style.tier]: layer === 'tier',
          [style.horizontalUnits]: layer === 'horizontalUnits',
          [style.verticalUnits]: layer === 'verticalUnits',
          [style.fieldUnits]: layer === 'fieldUnits',
          [style.showWellNames]: layer === 'showWellNames',
          [style.surfaceInterest]: layer === 'surfaceInterest',
          [style.workingInterest]: layer === 'workingInterest',
          [style.unleased]: layer === 'unleased',
          [style.royaltyInterest]: layer === 'royaltyInterest',
        });
        return (
          <div key={layer} className={checkboxClassName}>
            <Checkbox
              id={layer}
              labelText={startCase(layer)}
              checked={
                mapConfig[layerKey].options[
                  layer as keyof MapUserConfig[typeof layerKey]['options']
                ]
              }
              onChange={(_evt, { checked }: { checked: boolean }) => {
                setMapConfig((current) => {
                  const allChecked = Object.keys(
                    current[layerKey].options
                  ).every((el) => {
                    if (el === layer) return checked;
                    return current[layerKey].options[
                      el as keyof MapUserConfig[typeof layerKey]['options']
                    ];
                  });
                  return {
                    ...current,
                    [layerKey]: {
                      checked: allChecked,
                      options: {
                        ...current[layerKey].options,
                        [layer]: checked,
                      },
                    },
                  };
                });
              }}
            />
          </div>
        );
      })}
    </>
  );
};

const WellsV2LayerOption = ({
  mapConfigAtom,
}: {
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);

  // Define our categories and status filters
  const categories = {
    horizontal: {
      label: 'Horizontal',
      key: 'horizontal' as const,
    },
    vertical: {
      label: 'Vertical',
      key: 'vertical' as const,
    },
  };

  // Status filters that will appear under each category with their colors
  const statusFilters = [
    { key: 'permit', label: 'Permit', color: '#ff69b4' },
    { key: 'duc', label: 'DUC', color: '#fe9929' },
    { key: 'producing', label: 'Producing', color: '#4292c6' },
    {
      key: 'shutIn',
      label: 'Shut In',
      color: '#555555',
    }, // Darker gray
    { key: 'expiredPermit', label: 'Expired Permit', color: '#969696' },
    { key: 'inactive', label: 'Inactive', color: '#cccccc' },
    // The four Create-New-Well pre-evidence stages. `key` is the camelCase stem the
    // option keys are built from (horizontalPrePermit, verticalApd, …) and the SCSS
    // swatch class is keyed on it too.
    {
      key: 'prePermit',
      label: 'Pre-Permit',
      color: WELL_STATUS_COLOR_MAP.pre_permit,
    },
    { key: 'apd', label: 'APD', color: WELL_STATUS_COLOR_MAP.apd },
    {
      key: 'proposal',
      label: 'Proposal',
      color: WELL_STATUS_COLOR_MAP.proposal,
    },
    { key: 'planned', label: 'Planned', color: WELL_STATUS_COLOR_MAP.planned },
  ];

  // Helper function to determine checkbox state for categories
  const getCategoryCheckState = (
    categoryKey: string
  ): { checked: boolean; indeterminate: boolean } => {
    // Get all child filter keys for this category
    const childFilters = statusFilters.map((status) => {
      return `${categoryKey}${
        status.key.charAt(0).toUpperCase() + status.key.slice(1)
      }`;
    });

    // Check if all child filters are checked
    const allChildrenChecked = childFilters.every(
      (filter) =>
        mapConfig.wellsV2.options[
          filter as keyof typeof mapConfig.wellsV2.options
        ]
    );

    // Check if some but not all child filters are checked
    const someChildrenChecked = childFilters.some(
      (filter) =>
        mapConfig.wellsV2.options[
          filter as keyof typeof mapConfig.wellsV2.options
        ]
    );

    return {
      checked: allChildrenChecked,
      indeterminate: !allChildrenChecked && someChildrenChecked,
    };
  };

  const handleCategoryChange = (categoryKey: string, checked: boolean) => {
    setMapConfig((current) => {
      // Create a new config object
      const newConfig = {
        ...current,
        wellsV2: {
          ...current.wellsV2,
          options: {
            ...current.wellsV2.options,
            [categoryKey]: checked,
          },
        },
      };

      // If checking a category, check all its child filters
      if (checked) {
        // Check all child filters for this category
        statusFilters.forEach((status) => {
          const filterKey = `${categoryKey}${
            status.key.charAt(0).toUpperCase() + status.key.slice(1)
          }`;

          // Set each child filter to true
          newConfig.wellsV2.options[
            filterKey as keyof typeof newConfig.wellsV2.options
          ] = true;
        });
      }

      // If unchecking a category, also uncheck all its child filters
      if (!checked) {
        // Get all child filter keys for this category
        statusFilters.forEach((status) => {
          const filterKey = `${categoryKey}${
            status.key.charAt(0).toUpperCase() + status.key.slice(1)
          }`;

          // Set each child filter to false
          newConfig.wellsV2.options[
            filterKey as keyof typeof newConfig.wellsV2.options
          ] = false;
        });
      }

      // Check if any option is checked to determine overall layer visibility
      const anyOptionChecked = Object.values(newConfig.wellsV2.options).some(
        (value) => value
      );
      newConfig.wellsV2.checked = anyOptionChecked;

      return newConfig;
    });
  };

  return (
    <>
      {Object.values(categories).map((category) => (
        <div key={category.key}>
          <CollapsibleSection
            id={`wellsV2-${category.key}`}
            title={category.label}
            checked={getCategoryCheckState(category.key).checked}
            indeterminate={getCategoryCheckState(category.key).indeterminate}
            defaultOpen={category.key === 'horizontal'}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleCategoryChange(category.key, checked)
            }
            className={style.categoryHeader}
          >
            <div className={style.groupChecks}>
              {statusFilters.map((status) => {
                const filterKey = `${category.key}${
                  status.key.charAt(0).toUpperCase() + status.key.slice(1)
                }`;

                return (
                  <div
                    key={filterKey}
                    className={`${style.coloredFilter} ${style[status.key]}`}
                  >
                    <Checkbox
                      id={`wellsV2-${filterKey}`}
                      labelText={status.label}
                      checked={
                        mapConfig.wellsV2.options[
                          filterKey as keyof typeof mapConfig.wellsV2.options
                        ]
                      }
                      onChange={(_evt, { checked }: { checked: boolean }) => {
                        setMapConfig((current) => {
                          const newConfig = {
                            ...current,
                            wellsV2: {
                              ...current.wellsV2,
                              options: {
                                ...current.wellsV2.options,
                                [filterKey]: checked,
                              },
                            },
                          };

                          // IMPORTANT: If checking a child filter, ensure the parent category is also checked
                          if (checked) {
                            newConfig.wellsV2.options[category.key] = true;
                          }

                          // Ensure overall wellsV2.checked is true if any option is selected
                          const anyOptionChecked = Object.values(
                            newConfig.wellsV2.options
                          ).some((value) => value);
                          newConfig.wellsV2.checked = anyOptionChecked;

                          return newConfig;
                        });
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        </div>
      ))}
    </>
  );
};

type AllCheckState = 'CHECKED' | 'UNCHECKED' | 'INDETERMINATE';

const isWellsV2Config = (config: any): config is MapUserConfig['wellsV2'] => {
  return (
    'options' in config &&
    'surfaceHole' in config.options &&
    'bottomHole' in config.options &&
    'stick' in config.options &&
    'survey' in config.options
  );
};

const MapLayerSelector = ({
  mapConfigAtom,
  wellsV2MinZoom,
  currentZoom,
}: {
  mapConfigAtom: MapUserConfigAtom;
  wellsV2MinZoom?: number;
  currentZoom?: number;
}) => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);
  const wellsV2MinZoomOrDefault = wellsV2MinZoom ?? 10;
  const currentZoomOrDefault = currentZoom ?? 13;
  const getCheckState = <Options extends Record<string, boolean>>(
    options: Options
  ): AllCheckState => {
    const allChecked = Object.values(options).every((v) => v);
    const allUnchecked = Object.values(options).every((v) => !v);
    return allChecked
      ? 'CHECKED'
      : allUnchecked
        ? 'UNCHECKED'
        : 'INDETERMINATE';
  };

  // For wellsV2LayerState, only consider the child filter options, not the parent categories
  const wellsV2LayerState: AllCheckState = getCheckState({
    horizontalPermit: mapConfig.wellsV2.options.horizontalPermit,
    horizontalDuc: mapConfig.wellsV2.options.horizontalDuc,
    horizontalProducing: mapConfig.wellsV2.options.horizontalProducing,
    horizontalShutIn: mapConfig.wellsV2.options.horizontalShutIn,
    horizontalExpiredPermit: mapConfig.wellsV2.options.horizontalExpiredPermit,
    horizontalInactive: mapConfig.wellsV2.options.horizontalInactive,
    horizontalPrePermit: mapConfig.wellsV2.options.horizontalPrePermit,
    horizontalApd: mapConfig.wellsV2.options.horizontalApd,
    horizontalProposal: mapConfig.wellsV2.options.horizontalProposal,
    horizontalPlanned: mapConfig.wellsV2.options.horizontalPlanned,
    verticalPermit: mapConfig.wellsV2.options.verticalPermit,
    verticalDuc: mapConfig.wellsV2.options.verticalDuc,
    verticalProducing: mapConfig.wellsV2.options.verticalProducing,
    verticalShutIn: mapConfig.wellsV2.options.verticalShutIn,
    verticalExpiredPermit: mapConfig.wellsV2.options.verticalExpiredPermit,
    verticalInactive: mapConfig.wellsV2.options.verticalInactive,
    verticalPrePermit: mapConfig.wellsV2.options.verticalPrePermit,
    verticalApd: mapConfig.wellsV2.options.verticalApd,
    verticalProposal: mapConfig.wellsV2.options.verticalProposal,
    verticalPlanned: mapConfig.wellsV2.options.verticalPlanned,
  });
  const developmentLayerState: AllCheckState = getCheckState(
    mapConfig.developmentAreas.options
  );

  const basinTierLayerState: AllCheckState = getCheckState(
    mapConfig.basinTier.options
  );
  const titleEntityAssetsLayerState: AllCheckState = getCheckState(
    mapConfig.titleEntityAssets.options
  );

  const pipelineLayerState: AllCheckState = getCheckState(
    mapConfig.pipelines.options
  );

  const drillingRigsLayerState: AllCheckState = getCheckState(
    mapConfig.drillingRigs.options
  );

  const handleCheckStateChange = (
    layer: keyof typeof mapConfig,
    checked: boolean
  ) => {
    setMapConfig((current) => {
      const layerConfig = current[layer];

      if (layerConfig && 'options' in layerConfig) {
        if (layer === 'wellsV2' && isWellsV2Config(layerConfig)) {
          // For wellsV2, set all options to the checked state
          return {
            ...current,
            wellsV2: {
              ...layerConfig,
              checked,
              options: {
                // Set all options to the checked state
                horizontal: checked,
                vertical: checked,
                horizontalPermit: checked,
                horizontalDuc: checked,
                horizontalProducing: checked,
                horizontalShutIn: checked,
                horizontalExpiredPermit: checked,
                horizontalInactive: checked,
                horizontalPrePermit: checked,
                horizontalApd: checked,
                horizontalProposal: checked,
                horizontalPlanned: checked,
                verticalPermit: checked,
                verticalDuc: checked,
                verticalProducing: checked,
                verticalShutIn: checked,
                verticalExpiredPermit: checked,
                verticalInactive: checked,
                verticalPrePermit: checked,
                verticalApd: checked,
                verticalProposal: checked,
                verticalPlanned: checked,
                // Keep the old options for backward compatibility
                surfaceHole: layerConfig.options.surfaceHole,
                bottomHole: layerConfig.options.bottomHole,
                stick: checked ? true : layerConfig.options.stick,
                survey: checked ? true : layerConfig.options.survey,
                permit: checked,
                duc: checked,
                producing: checked,
                shutIn: checked,
                expiredPermit: checked,
                inactive: checked,
                prePermit: checked,
                apd: checked,
                proposal: checked,
                planned: checked,
                // showWellNames is now optional, so we don't need to handle it
              },
            },
          };
        }

        return {
          ...current,
          [layer]: {
            ...layerConfig,
            options: Object.keys(layerConfig.options).reduce(
              (acc, key) => ({ ...acc, [key]: checked }),
              {}
            ),
            checked,
          },
        };
      }

      return current;
    });
  };

  return (
    <div className={style.layersSelector}>
      <CollapsibleSection
        id="wells-v2-layers"
        title="Wells"
        defaultOpen={true}
        checked={wellsV2LayerState === 'CHECKED'}
        indeterminate={wellsV2LayerState === 'INDETERMINATE'}
        onChange={(_evt, { checked }: { checked: boolean }) =>
          handleCheckStateChange('wellsV2', checked)
        }
        disabled={currentZoomOrDefault < wellsV2MinZoomOrDefault}
      >
        <WellsV2LayerOption mapConfigAtom={mapConfigAtom} />
      </CollapsibleSection>

      <CollapsibleSection
        id="development-layers"
        title="Development Areas"
        checked={developmentLayerState === 'CHECKED'}
        indeterminate={developmentLayerState === 'INDETERMINATE'}
        onChange={(_evt, { checked }: { checked: boolean }) =>
          handleCheckStateChange('developmentAreas', checked)
        }
        defaultOpen={true}
      >
        <LayerOption
          layerKey="developmentAreas"
          mapConfigAtom={mapConfigAtom}
        />
      </CollapsibleSection>

      <CollapsibleSection
        id="title-entity-assets"
        title="Title Entity Assets"
        checked={titleEntityAssetsLayerState === 'CHECKED'}
        indeterminate={titleEntityAssetsLayerState === 'INDETERMINATE'}
        onChange={(_evt, { checked }: { checked: boolean }) => {
          handleCheckStateChange('titleEntityAssets', checked);
        }}
        defaultOpen={false}
      >
        <LayerOption
          layerKey="titleEntityAssets"
          mapConfigAtom={mapConfigAtom}
        />
      </CollapsibleSection>

      <CollapsibleSection
        id="portfolio-assets"
        title="Portfolio Assets"
        checked={mapConfig.portfolioAssets?.checked || false}
        isHeaderOnly={true}
        onChange={(_evt, { checked }: { checked: boolean }) => {
          setMapConfig((current) => ({
            ...current,
            portfolioAssets: {
              checked,
            },
          }));
        }}
      />

      <CollapsibleSection
        id="type-curves"
        title="Type Curves"
        checked={mapConfig.typeCurves?.checked || false}
        isHeaderOnly={true}
        onChange={(_evt, { checked }: { checked: boolean }) => {
          setMapConfig((current) => ({
            ...current,
            ['typeCurves']: {
              checked,
            },
          }));
        }}
      />

      <CollapsibleSection
        id="basin-tier-layers"
        title="Basin/Tier"
        checked={basinTierLayerState === 'CHECKED'}
        indeterminate={basinTierLayerState === 'INDETERMINATE'}
        onChange={(_evt, { checked }: { checked: boolean }) =>
          handleCheckStateChange('basinTier', checked)
        }
        defaultOpen={true}
      >
        <LayerOption layerKey="basinTier" mapConfigAtom={mapConfigAtom} />
      </CollapsibleSection>
      <CollapsibleSection
        id="drilling-rigs-layers"
        title="Drilling Rigs"
        checked={drillingRigsLayerState === 'CHECKED'}
        indeterminate={drillingRigsLayerState === 'INDETERMINATE'}
        onChange={(_evt, { checked }: { checked: boolean }) =>
          handleCheckStateChange('drillingRigs', checked)
        }
      >
        <DrillingRigSelector mapConfigAtom={mapConfigAtom} />
      </CollapsibleSection>

      <CollapsibleSection
        id="pipeline-layers"
        title="Pipelines"
        checked={pipelineLayerState === 'CHECKED'}
        indeterminate={pipelineLayerState === 'INDETERMINATE'}
        onChange={(_evt, { checked }: { checked: boolean }) => {
          setMapConfig((current) => ({
            ...current,
            pipelines: {
              ...current.pipelines,
              checked,
              options: {
                gasOperating: checked,
                gasUnderConstruction: checked,
                gasPlannedOther: checked,
                oilOperating: checked,
                oilUnderConstruction: checked,
                oilPlannedOther: checked,
                productOperating: checked,
                productConstruction: checked,
                productPlannedOther: checked,
                unknownOperating: checked,
                unknownUnderConstruction: checked,
                unknownPlannedOther: checked,
              },
            },
          }));
        }}
      >
        <PipelineSelector mapConfigAtom={mapConfigAtom} />
      </CollapsibleSection>

      <FocusAreaSelector mapConfigAtom={mapConfigAtom} />
      <StructureGridSelector mapConfigAtom={mapConfigAtom} />

      <h3 className={style.label}>Settings</h3>
      <div className={style.titleArea}>
        <div className={style.allCheckBox}>
          <Checkbox
            id={'popups'}
            labelText="Popups"
            checked={mapConfig.popups.checked}
            onChange={(_evt, { checked }: { checked: boolean }) => {
              setMapConfig((current) => ({
                ...current,
                ['popups']: {
                  checked,
                },
              }));
            }}
          />
        </div>
      </div>
      <div className={style.titleArea}>
        <div className={style.allCheckBox}>
          <Checkbox
            id={'scrollZoom'}
            labelText="Scroll Zoom"
            checked={mapConfig.scrollZoom.checked}
            onChange={(_evt, { checked }: { checked: boolean }) => {
              setMapConfig((current) => ({
                ...current,
                ['scrollZoom']: {
                  checked,
                },
              }));
            }}
          />
        </div>
      </div>
    </div>
  );
};

export { MapLayerSelector };
